/**
 * ListingAbout — the assembled "About this home" section (SEO F6).
 *
 * SSR-rendered visible prose, not meta-only and not behind a tab: the
 * crawler and the reader must see the same words, which is the whole point
 * of assembling it server-side from stored facts.
 *
 * This component makes no editorial decisions. WHICH sentences exist, and
 * whether there are any at all, was settled backend-side by the closed
 * paragraph set (`listings/seo/prose.rs`); an empty list means the listing
 * holds too few facts to say anything true, and then this renders nothing
 * rather than a "details coming soon" stub.
 *
 * Distinct from `ListingDescription`, which is the OWNER's own free text —
 * that stays exactly as written, in whichever language they wrote it.
 */
import { useTranslations } from '@/shared/i18n/useTranslations';
import { plaintextString } from '@letsgethome/types/branded';

type Props = {
  /** Already locale-resolved by the backend. */
  paragraphs: readonly string[];
};

export function ListingAbout({ paragraphs }: Props) {
  const t = useTranslations('listings.detail');
  if (paragraphs.length === 0) {
    return null;
  }
  return (
    <section className="space-y-3">
      <h2 className="text-xl font-semibold">{t('about.title')}</h2>
      {paragraphs.map((paragraph) => (
        <p key={paragraph} className="text-muted-foreground">
          {plaintextString(paragraph)}
        </p>
      ))}
    </section>
  );
}
